import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@/lib/utils";

const Progress = React.forwardRef(({ 
    className, 
    value, 
    max = 100,
    indicateComplete = true,
    showValue = false,
    variant = "default",
    size = "default",
    ...props 
}, ref) => {
    // Calculate percentage
    const percentage = value != null ? Math.min(Math.max(0, (value / max) * 100), 100) : 0;
    
    // Determine color based on variant
    const getVariantStyles = () => {
        switch (variant) {
            case "success":
                return "bg-green-600";
            case "error":
                return "bg-red-600";
            case "warning":
                return "bg-yellow-600";
            default:
                return percentage === 100 && indicateComplete
                    ? "bg-green-600"
                    : "bg-blue-600";
        }
    };

    // Determine size styles
    const getSizeStyles = () => {
        switch (size) {
            case "sm":
                return "h-2";
            case "lg":
                return "h-4";
            default:
                return "h-3";
        }
    };

    return (
        <div className="relative w-full">
            <ProgressPrimitive.Root
                ref={ref}
                className={cn(
                    "relative w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800",
                    getSizeStyles(),
                    className
                )}
                {...props}
            >
                <ProgressPrimitive.Indicator
                    className={cn(
                        "h-full w-full flex-1 transition-all",
                        getVariantStyles(),
                        percentage === 0 ? "duration-0" : "duration-300 ease-in-out",
                        percentage === 100 && "animate-pulse"
                    )}
                    style={{ transform: `translateX(-${100 - percentage}%)` }}
                />
            </ProgressPrimitive.Root>
            
            {showValue && (
                <div className="absolute right-0 -top-6 text-sm font-medium">
                    {Math.round(percentage)}%
                </div>
            )}
        </div>
    );
});

Progress.displayName = "Progress";

export { Progress };
